import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

// Components
import { Select, Form, ToggleSwitch, Checkbox, Pill, Input } from 'Components';
import { useFeatureEnabled } from 'Components/FeatureToggle';
import { Alert, Divider, Grid } from '@mui/material';

// Helpers
import TriggerLandmark from 'Modules/alerts/components/wizardAlert/common/components/TriggerLandmark';

export const Inspection = (props) => {

  const { value, onChange, formRef, triggers, triggersRaw } = props;
  const { messages, formatMessage } = useIntl();
  const [showErrorTrigger, setShowErrorTrigger] = useState(false);
  const isConfigTypeToggleEnabled = useFeatureEnabled('evir-alert-enabled-config-type', false);

  const defectSeverities = [
    { id: 'Critical', label: messages['alertsNewAlert_Critical'] },
    { id: 'Minor', label: messages['alertsNewAlert_Minor'] },
  ];
  const inspectionStatuses = [
    { id: 'Incomplete', label: messages['alertsNewAlert_IncompleteInspection'] },
    { id: 'Unverified', label: messages['alertsNewAlert_UnverifiedInspection'] },
  ];
  const inspectionConfigTypes = [
    { id: 'Pre-Trip', label: messages['alertsNewAlert_PreTripInspection'] },
    { id: 'Post-Trip', label: messages['alertsNewAlert_PostTripInspection'] },
    { id: 'Annual', label: messages['alertsNewAlert_AnnualInspection'] },
    { id: '90-Day', label: messages['alertsNewAlert_90DayInspection'] },
    { id: 'Daily', label: messages['alertsNewAlert_DailyInspection'] },
    { id: 'Monthly', label: messages['alertsNewAlert_MonthlyInspection'] },
  ];
  const components = [
    { id: 'Brakes', label: messages['alertsNewAlert_Brakes'] },
    { id: 'Tires', label: messages['alertsNewAlert_Tires'] },
    { id: 'Lights', label: messages['alertsNewAlert_Lights'] },
    { id: 'Engine', label: messages['alertsNewAlert_Engine'] },
    { id: 'Transmission', label: messages['alertsNewAlert_Transmission'] },
    { id: 'Steering', label: messages['alertsNewAlert_Steering'] },
    { id: 'Suspension', label: messages['alertsNewAlert_Suspension'] },
    { id: 'Body', label: messages['alertsNewAlert_Body'] },
    { id: 'Safety Equipment', label: messages['alertsNewAlert_SafetyEquipment'] },
  ];
  const inspectionTypes = [
    { id: 'Any', label: messages['alertsNewAlert_Any'] },
    { id: 'Pre-Trip', label: messages['alertsNewAlert_PreTrip'] },
    { id: 'Post-Trip', label: messages['alertsNewAlert_PostTrip'] },
  ];
  const triggerTypes = [
    { code: 'InspectionStatus', name: messages['alertsNewAlert_InspectionStatus'] },
    { code: 'InspectionDefects', name: messages['alertsNewAlert_InspectionDefects'] },
    { code: 'InspectionDuration', name: messages['alertsNewAlert_InspectionDuration'] },
  ];

  const handleSpecificInspectionConfigTypeToggleChange = (event) => {
    const { inspectionConfigTypes, ...rest } = value;
    onChange({
      ...rest,
      ...(event ? { inspectionConfigTypes: [] } : {}),
    });
  };

  const handleInspectionConfigTypeCheckboxChange = (configTypeId, checked) => {
    const newInspectionConfigTypes = checked
      ? [...(value.inspectionConfigTypes || []), configTypeId]
      : (value.inspectionConfigTypes || []).filter((id) => id !== configTypeId);
    onChange({
      ...value,
      inspectionConfigTypes: newInspectionConfigTypes,
    });
  };

  const handleInspectionStatusCheckboxChange = (statusId, checked) => {
    const { triggerStatus, ...rest } = value;
    const newInspectionStatuses = checked
      ? [...(triggerStatus || []), statusId]
      : (triggerStatus || []).filter((id) => id !== statusId);
    onChange({
      ...rest,
      ...(newInspectionStatuses.length > 0 ? { triggerStatus: newInspectionStatuses } : {}),
    });
  };

  const handleDefectSeverityCheckboxChange = (severityId, checked) => {
    const { triggerDefects, ...rest } = value;
    const newDefectSeverities = checked
      ? [...(triggerDefects || []), severityId]
      : (triggerDefects || []).filter((id) => id !== severityId);
    onChange({
      ...rest,
      ...(newDefectSeverities.length > 0 ? { triggerDefects: newDefectSeverities } : {}),
    });
  };

  const handleIncludeAllComponentsCheckboxChange = (checked) => {
    const { components, ...rest } = value;
    onChange({
      ...rest,
      ...(!checked ? { components: [] } : {}),
    });
  };

  const handleComponentCheckboxChange = (componentId, checked) => {
    const newComponents = checked
      ? [...(value.components || []), componentId]
      : (value.components || []).filter((id) => id !== componentId);
    onChange({
      ...value,
      components: newComponents,
    });
  };

  const handleTriggerTypeSelectChange = (selected) => {
    let remainingFields = {};

    if (selected?.code === 'InspectionStatus') {
      const { triggerDefects, triggerDuration, components, ...rest } = value;
      remainingFields = { ...rest };
    }

    if (selected?.code === 'InspectionDefects') {
      const { triggerDuration, triggerStatus, ...rest } = value;
      remainingFields = { ...rest };
    }

    if (selected?.code === 'InspectionDuration') {
      const { triggerDefects, triggerStatus, components, ...rest } = value;
      remainingFields = {
        ...rest,
        triggerDuration: {
          inspectionType: 'Any',
          thresholdType: 'Over',
          value: 30,
          duration: 'Minutes',
        },
      };
    }

    onChange({
      ...remainingFields,
      triggerType: selected?.code || '',
    });
  };

  const handleInspectionTypeSelectChange = (selected) => {
    onChange({
      ...value,
      triggerDuration: {
        ...value.triggerDuration,
        inspectionType: selected?.id || '',
      },
    });
  };

  const handleThresholdTypeChange = (type) => {
    onChange({
      ...value,
      triggerDuration: {
        ...value.triggerDuration,
        thresholdType: type,
      },
    });
  };

  const handleDurationChange = (event) => {
    onChange({
      ...value,
      triggerDuration: {
        ...value.triggerDuration,
        value: parseInt(event, 10),
      },
    });
  };

  const getMessage = (data) => {
    const { triggerType } = data;

    switch (triggerType) {
      case 'InspectionDefects':
        const { triggerDefects } = data;
        return formatMessage(
          { id: 'alertsNewAlert_InspectionDefects_Message' },
          {
            defectSeverities: (triggerDefects || []).map((severityId) => messages[`alertsNewAlert_${severityId}`]).join(', '),
          },
        );
      case 'InspectionStatus':
        const { triggerStatus } = data;
        const count = triggerStatus?.length || 0;

        if (count === 0) {
          return messages['alertsNewAlert_InspectionStatus_Message_NoStatuses'];
        }

        return formatMessage(
          { id: 'alertsNewAlert_InspectionStatus_Message' },
          {
            inspectionStatuses: (triggerStatus || [])
              .map((statusId) => messages[`alertsNewAlert_${statusId}`])
              .sort((a, b) => a.localeCompare(b))
              .join(' or '),
          },
        );
      case 'InspectionDuration':
        return messages['alertsNewAlert_InspectionDuration_Message'];
      default:
        return '';
    }
  };

  return (
    <Form ref={formRef}
      onSubmit={() => {
        const isInspectionStatusesValid = value.triggerType === 'InspectionStatus'
          ? value.triggerStatus?.length > 0
          : true;
        const isValid = value.trigger ? triggersRaw?.length > 0 : true;

        if (!isValid) {
          setShowErrorTrigger(true);
        } else {
          setShowErrorTrigger(false);
        }

        return isInspectionStatusesValid && isValid;
      }}
    >
      <Select
        label={messages['alertsNewAlert_TriggerType']}
        placeholder={messages['alertsNewAlert_TriggerTypePlaceholder']}
        requiredMessage={messages['required_field']}
        value={triggerTypes.find((option) => option.code === value.triggerType)}
        onChange={(selected) => handleTriggerTypeSelectChange(selected)}
        id='triggerTypeSelect'
        options={triggerTypes}
        textField='name'
        valueField='code'
        required
      />

      {value.triggerType === 'InspectionStatus' && (
        <div className='inspection-status-container'>
          <label>
            {messages['alertsNewAlert_InspectionStatus']}
            <span className='text-danger'> *</span>
          </label>
          <Grid>
            {
              inspectionStatuses.map((inspectionStatus) => (
                <Grid key={inspectionStatus.id} sm={6}>
                  <Checkbox
                    id={`inspectionStatusCheckbox_${inspectionStatus.id}`}
                    label={inspectionStatus.label}
                    value={value.triggerStatus?.includes(inspectionStatus.id) || false}
                    onChange={(checked) => handleInspectionStatusCheckboxChange(inspectionStatus.id, checked)}
                    size='md'
                  />
                </Grid>
              ))
            }
          </Grid>
          {
            (value.triggerStatus || []).length === 0 &&
            <small className='text-danger'> {messages['alertsNewAlert_InspectionStatusesValidation']}</small>
          }
        </div>
      )}

      {value.triggerType === 'InspectionDefects' && (
        <div className='defect-severity-container'>
          <label>{messages['alertsNewAlert_DefectSeverity']}</label>
          <Grid>
            {
              defectSeverities.map((defectSeverity) => (
                <Grid key={defectSeverity.id} sm={6}>
                  <Checkbox
                    id={`defectSeverityCheckbox_${defectSeverity.id}`}
                    label={defectSeverity.label}
                    value={value.triggerDefects?.includes(defectSeverity.id) || false}
                    onChange={(checked) => handleDefectSeverityCheckboxChange(defectSeverity.id, checked)}
                    size='md'
                  />
                </Grid>
              ))
            }
          </Grid>
        </div>
      )}

      {value.triggerType === 'InspectionDuration' && (
        <div className='inspection-duration-container'>
          {/* Hidden for EVIRPX-557: Inspection Type dropdown */}
          {/* <Select
            label={messages['alertsNewAlert_InspectionType']}
            value={inspectionTypes.find((option) => option.id === value.triggerDuration?.inspectionType)}
            onChange={(selected) => handleInspectionTypeSelectChange(selected)}
            id='triggerTypeSelect'
            options={inspectionTypes}
            textField='label'
            valueField='id'
            className='inspection-type-select'
          /> */}

          <div style={{ paddingBottom: 16 }}>
            <label className="input-normal-label">{messages['alertsNewAlert_ThresholdType']}</label>
            <Grid container spacing={2} alignItems="center">
              <Grid item sm={6}>
                <Pill
                  textField={'label'}
                  item={{ id: 'Under', label: messages['alertsNewAlert_Under'] }}
                  outlined={value.triggerDuration?.thresholdType !== 'Under'}
                  onClick={() => handleThresholdTypeChange('Under')}
                  style={{ border: '0', margin: '2px 0', width: '100%' }}
                  label="pill-label-text"
                  colorField="default"
                  colorSelection="005bf8"
                />
              </Grid>
              <Grid item sm={6}>
                <Pill
                  textField={'label'}
                  item={{ id: 'Over', label: messages['alertsNewAlert_Over'] }}
                  outlined={value.triggerDuration?.thresholdType !== 'Over'}
                  onClick={() => handleThresholdTypeChange('Over')}
                  style={{ border: '0', margin: '2px 0', width: '100%' }}
                  label="pill-label-text"
                  colorField="default"
                  colorSelection="005bf8"
                />
              </Grid>
            </Grid>
          </div>

          <div>
            <label className="input-normal-label">{messages['alertsNewAlert_ExpectedDurationMinutes']}</label>
            <Input
              type='number'
              value={value.triggerDuration?.value}
              onChange={handleDurationChange}
              onlyPositive
              preventOnChange
              id='durationInput'
            />
          </div>
        </div>
      )}

      {value.triggerType && (
        <div style={{ paddingTop: 20 }}>
          <Divider></Divider>

          {isConfigTypeToggleEnabled && (
            <div>
              <div style={{ padding: '20px 0 0 0', fontWeight: 300 }}>{messages['alertsNewAlert_OptionalFilters']}</div>

              <div style={{ paddingTop: 20 }}>
                <ToggleSwitch
                  id='specificConfigTypeToggle'
                  small
                  blueToggle
                  optionLabels={['', '']}
                  label={messages['alertsNewAlert_ApplyToSpecificInspectionConfigType']}
                  checked={value.inspectionConfigTypes !== undefined}
                  onChange={(event) => handleSpecificInspectionConfigTypeToggleChange(event)}
                />

                {value.inspectionConfigTypes && (
                  <Grid style={{ padding: '20px 20px 0 20px' }}>
                    <div>{messages['alertsNewAlert_SelectInspectionConfigTypes']}</div>
                    <Grid container>
                      {
                        inspectionConfigTypes.map((inspectionConfigType) => (
                          <Grid item key={inspectionConfigType.id} sm={6}>
                            <Checkbox
                              id={`configTypeCheckbox_${inspectionConfigType.id}`}
                              label={inspectionConfigType.label}
                              value={value.inspectionConfigTypes?.includes(inspectionConfigType.id) || false}
                              onChange={(checked) => handleInspectionConfigTypeCheckboxChange(inspectionConfigType.id, checked)}
                              size='md'
                            />
                          </Grid>
                        ))
                      }
                    </Grid>
                  </Grid>
                )}
              </div>

              {value.triggerType === 'InspectionDefects' && (
                <Grid style={{ paddingTop: 11 }}>
                  <Checkbox
                    id={'includeAllComponentsCheckbox'}
                    label={messages['alertsNewAlert_IncludeAllComponents']}
                    value={value.components === undefined}
                    onChange={(checked) => handleIncludeAllComponentsCheckboxChange(checked)}
                    size='md'
                  />

                  {value.components && (
                    <Grid style={{ padding: '11px 20px 0 20px' }}>
                      <div>{messages['alertsNewAlert_SelectComponents']}</div>
                      <Grid container>
                        {
                          components.map((component) => (
                            <Grid item key={component.id} sm={6}>
                              <Checkbox
                                id={`componentCheckbox_${component.id}`}
                                label={component.label}
                                value={value.components?.includes(component.id) || false}
                                onChange={(checked) => handleComponentCheckboxChange(component.id, checked)}
                                size='md'
                              />
                            </Grid>
                          ))
                        }
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </div>
          )}

          <TriggerLandmark config={value} triggers={triggers} triggersRaw={triggersRaw} onChange={onChange} showErrorTrigger={showErrorTrigger} />

          <Grid style={{ padding: '20px 0 10px 0' }}>
            <Alert severity='info' className='custom-info-alert'>{getMessage(value)}</Alert>
          </Grid>
        </div>
      )}

      <Divider style={{ marginTop: 20 }}></Divider>
    </Form>
  );
};
